"use strict";
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __using = (stack, value, async) => {
  if (value != null) {
    if (typeof value !== "object" && typeof value !== "function") __typeError("Object expected");
    var dispose, inner;
    if (async) dispose = value[__knownSymbol("asyncDispose")];
    if (dispose === void 0) {
      dispose = value[__knownSymbol("dispose")];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") __typeError("Object not disposable");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    stack.push([async, dispose, value]);
  } else if (async) {
    stack.push([async]);
  }
  return value;
};
var __callDispose = (stack, error, hasError) => {
  var E = typeof SuppressedError === "function" ? SuppressedError : function(e, s, m, _) {
    return _ = Error(m), _.name = "SuppressedError", _.error = e, _.suppressed = s, _;
  };
  var fail = (e) => error = hasError ? new E(e, error, "An error was suppressed during disposal") : (hasError = true, e);
  var next = (it) => {
    while (it = stack.pop()) {
      try {
        var result = it[1] && it[1].call(it[2]);
        if (it[0]) return Promise.resolve(result).then(next, (e) => (fail(e), next()));
      } catch (e) {
        fail(e);
      }
    }
    if (hasError) throw error;
  };
  return next();
};
/**
 * @license
 * Copyright 2019 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  normalizeListResponse
} from "#src/kvstore/index.js";
import { isS3ListResponse } from "#src/kvstore/s3/list.js";
import { encodePathForUrl, extractQueryAndFragment } from "#src/kvstore/url.js";
import { fetchOk } from "#src/util/http_request.js";
import {
  ProgressSpan
} from "#src/util/progress_listener.js";
export async function getHtmlDirectoryListing(url, options = {}) {
  const baseUrl = extractQueryAndFragment(url).base;
  const { fetchOkImpl = fetchOk, signal, progressListener } = options;
  const response = await fetchOkImpl(
    url,
    /*init=*/
    {
      headers: { accept: "text/html" },
      signal,
      progressListener
    }
  );
  const contentType = response.headers.get("content-type");
  if (contentType === null || /\btext\/html\b/i.exec(contentType) === null) {
    throw new Error(`HTML directory listing not supported`);
  }
  const text = await response.text();
  if (isS3ListResponse(text)) {
    throw new Error(
      `HTML directory listing not supported, S3-compatible API detected`
    );
  }
  const doc = new DOMParser().parseFromString(text, "text/html");
  const nodes = doc.evaluate(
    "//a/@href",
    doc,
    null,
    XPathResult.UNORDERED_NODE_SNAPSHOT_TYPE,
    null
  );
  const results = /* @__PURE__ */ new Set();
  for (let i = 0, n = nodes.snapshotLength; i < n; ++i) {
    const node = nodes.snapshotItem(i);
    const href = node.textContent;
    if (href === null) continue;
    const withoutQuery = extractQueryAndFragment(href).base;
    if (withoutQuery) {
      const resolvedUrl = new URL(withoutQuery, baseUrl).toString();
      if (resolvedUrl.startsWith(baseUrl) && resolvedUrl !== baseUrl) {
        results.add(resolvedUrl);
      }
    }
  }
  return Array.from(results);
}
export async function listFromHtmlDirectoryListing(baseUrl, prefix, fetchOkImpl, options) {
  var _stack = [];
  try {
    const { progressListener } = options;
    const _span = __using(_stack, progressListener && new ProgressSpan(progressListener, {
      message: `Requesting HTML directory listing for ${baseUrl}`
    }));
    const { base, queryAndFragment } = extractQueryAndFragment(baseUrl);
    const baseAndPrefix = base + encodePathForUrl(prefix);
    const fullUrl = baseAndPrefix + queryAndFragment;
    const m = fullUrl.match(/^([a-z]+:\/\/.*\/)([^/?#]*)$/);
    if (m === null) {
      throw new Error(`Invalid HTTP URL: ${fullUrl}`);
    }
    const [, directoryUrl] = m;
    const listing = await getHtmlDirectoryListing(
      directoryUrl + queryAndFragment,
      {
        fetchOkImpl,
        signal: options.signal,
        progressListener: options.progressListener
      }
    );
    const entries = [];
    const directories = [];
    for (const entry of listing) {
      if (!entry.startsWith(baseAndPrefix)) continue;
      const p = decodeURIComponent(entry.substring(base.length));
      if (p.endsWith("/")) {
        directories.push(p.substring(0, p.length - 1));
      } else {
        entries.push({ key: p });
      }
    }
    return normalizeListResponse({ entries, directories });
  } catch (_) {
    var _error = _, _hasError = true;
  } finally {
    __callDispose(_stack, _error, _hasError);
  }
}
